<template>
  <div class="footer-links">
    <template v-if="isTabletOrDesktop">
      <div
        v-for="(section, index) in sections"
        :key="'section-' + index"
        class="footer-links__desktop"
      >
        <div class="footer-links__title">
          {{ $t(section.key) }}
        </div>
        <ul class="footer-links__section">
          <li
            v-for="link of section.links"
            v-show="typeof link.checkVisibility === 'undefined' || link.checkVisibility()"
            :key="link.id"
            class="footer-links__item"
            :class="{
              'footer-links__subtitle': link.subtitle,
              'footer-links__link': !link.subtitle,
            }"
            @click="$emit('navigation', link)"
          >
            <FooterLink
              class="footer-links__anchor"
              :class="{ 'footer-links__anchor--subtitle': link.subtitle }"
              :href="getHref(link)"
              :text="$t(link.key)"
              :isSpa="isSpa && link.isSpa"
            />
          </li>
        </ul>
      </div>
    </template>

    <Accordion v-else class="footer-links__mobile">
      <AccordionItem
        v-for="(section, index) in sections"
        :key="index"
        :id="`link-${index}`"
        class="footer-links__section footer-links__section--mobile"
        :index="1"
        :label="$t(section.key)"
        :isClosingOnClickOfContent="true"
      >
        <ul :key="'section' + index + 1">
          <li
            v-for="link of section.links"
            v-show="typeof link.checkVisibility === 'undefined' || link.checkVisibility()"
            :key="link.id"
            class="footer-links__item"
            :class="{
              'footer-links__title': link.title,
              'footer-links__subtitle': link.subtitle,
              'footer-links__link': !link.title && !link.subtitle,
            }"
          >
            <a
              class="footer-links__anchor"
              :class="{ 'footer-links__anchor--subtitle': link.subtitle }"
              :href="getHref(link)"
              >{{ $t(link.key) }}</a
            >
          </li>
        </ul>
      </AccordionItem>
    </Accordion>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Accordion from '@/components/_shared/Accordion.vue'
import AccordionItem from '@/components/_shared/AccordionItem/AccordionItem.vue'
import FooterLink from '@/components/Footer/FooterLink.vue'
import sections from './footer-links'

export default {
  name: 'FooterLinks',
  components: { Accordion, AccordionItem, FooterLink },
  props: {
    isTabletOrDesktop: Boolean,
  },

  data() {
    return {
      sections,
    }
  },

  computed: {
    ...mapState('page', ['isSpa']),
    ...mapGetters({
      locale: 'page/locale',
    }),
  },

  methods: {
    getHref(link) {
      return link.hasLocale === false
        ? `https://${link.baseUrl}${link.path}`
        : `https://${link.baseUrl}/${this.locale}${link.path}`
    },
  },
}
</script>

<style scoped lang="scss">
@import './footer-settings';
$link-bottom-padding: 6px;
$link-bottom-padding-desktop: 4px;

.footer-links {
  color: $c-white;

  @media ($mq-medium) {
    display: flex;
    flex: 1;
  }

  &__section {
    flex: 1;

    @media ($mq-medium) {
      padding-right: 80px;
    }

    @media ($mq-high) {
      display: inline-block;
      flex: initial;
    }
  }

  &__title {
    margin-bottom: 16px;
    font-weight: bold;
    font-size: $f-size-xlarge;
    line-height: 1.5;
    letter-spacing: 0.2px;
  }

  &__subtitle {
    margin-top: 20px;
    font-size: $f-size-small;
    font-weight: bold;
  }

  &__link {
    font-size: $f-size-small;
    line-height: 1.5;

    &:first-child {
      .footer-links__anchor {
        padding-top: 0;
      }
    }
  }

  &__anchor {
    display: inline-block;
    padding: $link-bottom-padding 0;
    color: $color-grey-mid-2;
    transition: color $transition-time;

    @media ($mq-full) {
      padding: $link-bottom-padding-desktop 0;
    }

    &:hover {
      color: $c-white;
    }

    &--subtitle {
      color: $c-white;
    }
  }

  /deep/ .accordion-item__inner {
    padding: 17px 0;

    &:hover {
      .accordion-item__label {
        color: $c-white;
      }
    }
  }

  /deep/ .accordion-item__content {
    padding: 12px 10px 8px;
  }

  /deep/ .accordion-item__label {
    padding: 0;
    font-size: $f-size-larger;

    &:before {
      padding: 17px 0;
    }
  }

  /deep/ .accordion-item__cross {
    top: calc(50% + 1px);
  }
}
</style>

<i18n src="@/locales/footer_links.json">
</i18n>
